import {cn} from '@/shared/utils/format'
import {cva} from 'class-variance-authority'
import {Icon} from '@/components/atoms'
import {useEffect} from 'react'
import {createPortal} from 'react-dom'
import PropTypes from 'prop-types'

/**
 * Alert component to display different types of alert messages.
 *
 * @param {Object} props - The properties object.
 * @param {'success' | 'error' | 'info' | 'warning'} props.type - The type of alert to display.
 * @param {string} props.message - The message to display in the alert.
 * @param {boolean} [props.show=false] - Whether the alert is visible or not.
 * @param {Function} props.onCloseClick - The function to call when the close button is clicked.
 *
 * @returns {JSX.Element|null} The Alert component or null if not visible.
 */
const Alert = ({type, message, show = false, onCloseClick}) => {
  useEffect(() => {
    const itv = setTimeout(() => onCloseClick?.(), 5000)
    return () => clearTimeout(itv)
  }, [message, onCloseClick, show, type])

  const alertVariants = cva(
    `fixed right-4 top-6 z-[9999] flex w-full items-center rounded-md border px-5 py-4 text-text-secondary alert max-w-[calc(100vw-7.5rem)]`,
    {
      variants: {
        type: {
          success: 'min-h-[72px] border-[#55D050] bg-[#052F05]',
          error: 'border-transparent bg-support-error-primary',
          info: 'border-transparent bg-support-info-primary',
          warning: 'border-transparent bg-support-warning-primary',
        },
      },
    }
  )

  if (!show) {
    return null
  }

  return createPortal(
    <div data-testid={`alert-${type}`} className={cn(alertVariants({type}))} role="alert">
      <div className={`inline-flex h-6 w-6 items-center justify-center rounded-lg ${type === 'success' ? 'text-[#55D050]' : ''}`}>
        {type === 'success' && <Icon icon="check_circle_outline" size="medium" className="text-[#55D050]" />}
        {type === 'error' && <Icon icon="error" size="medium" className="text-button-icon" />}
        {type === 'info' && <Icon icon="check_circle_outline" size="medium" className="text-button-icon" />}
        {type === 'warning' && <Icon icon="warning_amber" size="medium" className="text-text-primary" />}
      </div>
      <div className="text-md ml-3 flex w-full items-center">
        {message && <div className={`whitespace-pre-line text-sm ${type === 'warning' ? 'text-black' : 'text-white'}`}>{message}</div>}
      </div>
      <button
        type="button"
        className="mt-1 inline-flex h-4 w-4 items-center justify-center text-icon"
        data-dismiss-target={`#alert-${type}`}
        data-testid="alert-close-btn"
        aria-label="Close"
        onClick={() => onCloseClick?.()}
      >
        <span className="sr-only">Close</span>
        <Icon icon="close" size="small" className={type === 'warning' ? 'text-text-primary' : 'text-button-icon'} />
      </button>
    </div>,
    document.body,
  )
}

Alert.propTypes = {
  type: PropTypes.oneOf(['success', 'error', 'info', 'warning']),
  message: PropTypes.string,
  show: PropTypes.bool,
  onCloseClick: PropTypes.func,
}

export default Alert
